import { compact } from 'lodash';

import * as K from 'common/constants';
import { FacetsCompletionInfo, Settings } from 'common/types';
import { CellType, Column as ColumnType, LogEntry, SystemField } from 'common/graphqlTypes';
import { FIELD_TYPE } from 'schemas/types';
import { formatAsCsvContent } from 'common/util/string';
import { getPQDisplayText, getPQValue, PQDataValue } from 'common/util/pqUtil';

import { FIELD_CATEGORIES, isMeaningfulValue, prepareDisplayedFieldsSelectors } from 'components/search/logLineUtils';

export type FormatterOptions = {
  timezone?: string | null;
  filter?: string;
  shortUrl?: string;
  facetInfo?: FacetsCompletionInfo;
};

// ***** Search results helper functions ***** //

// Format a log line for download/copy to clipboard. It should look very similar to the displayed log line.
const formatLogEntryAsText = (logEntry: LogEntry, settings: Settings, options: FormatterOptions): string => {
  const formatSettings = { ...settings, timezone: options.timezone || null };
  return (
    prepareDisplayedFieldsSelectors(logEntry, formatSettings)
      .map(({ category, select }) => {
        const logValue = select(logEntry, formatSettings);
        if (!logValue) {
          return null;
        }
        if (category === FIELD_CATEGORIES.SYSTEM) {
          return logValue.value;
        }
        if (category === FIELD_CATEGORIES.CUSTOM) {
          return `${logValue.type}=${logValue.value}`;
        }
        return null;
      })
      .filter(isMeaningfulValue)
      .join(' ')
      .replace(/\n/g, '\\n') + '\n'
  );
};

// Return an object that represents a log line.
const formatLogEntryAsJson = (
  logEntry: LogEntry,
  settings: Settings,
  options: FormatterOptions,
): { [key: string]: any } => {
  const formatSettings = { ...settings, timezone: options.timezone || null };
  return prepareDisplayedFieldsSelectors(logEntry, formatSettings)
    .map(({ category, select }) => {
      const logValue = select(logEntry, formatSettings);
      if (!logValue) {
        return null;
      }
      if (category === FIELD_CATEGORIES.SYSTEM || category === FIELD_CATEGORIES.CUSTOM) {
        return logValue;
      }
      return null;
    })
    .filter(Boolean)
    .reduce((acc, logValue) => {
      if (logValue) {
        return { ...acc, [logValue.type]: logValue.value };
      }
      return acc;
    }, {});
};

// Return a list of PQ-style columns based on settings and the list of search results. It should match the fields that are
// displayed to the user. If the user hasn't chosen fields to display, we include any field that appears in the results.
const getSearchResultColumns = (
  logObjects: Array<Record<string, any>>,
  settings: Settings,
  options?: FormatterOptions,
): ColumnType[] => {
  const getColumn = (field: string): ColumnType =>
    ({
      name: field,
      type: options?.facetInfo?.[field]?.type === FIELD_TYPE.NUMBER ? CellType.NUMBER : CellType.STRING,
    } as ColumnType);

  const systemFields = settings[K.SETTING.SYSTEM_FIELDS];
  const userDefinedFields = settings[K.SETTING.ADDITIONAL_FIELDS];
  const columns = [SystemField.date, SystemField.timestamp, SystemField.host, SystemField.logfile]
    .filter((col) => systemFields.includes(col))
    .map(getColumn);

  if (userDefinedFields?.length > 0) {
    columns.push(...userDefinedFields.map(getColumn));
  } else {
    const dataColumns = new Set<string>();
    const definedSystemFields = Object.values(SystemField);
    logObjects.forEach((log) => {
      Object.keys(log).forEach((key) => {
        if (!definedSystemFields.includes(key as SystemField)) {
          dataColumns.add(key);
        }
      });
    });
    columns.push(...Array.from(dataColumns).sort().map(getColumn));
  }
  if (systemFields.includes(SystemField.rawLogLine)) {
    columns.push(getColumn(SystemField.rawLogLine));
  }

  return columns;
};

// ***** Power query results helper functions ***** //

// format PQ results for display
const getPowerQueryValues = (rows: PQDataValue[][], columns: ColumnType[], options: FormatterOptions) =>
  rows.map((row) =>
    row.map((value, index) =>
      getPQDisplayText(value, columns[index], { skipCommas: true, timezone: options.timezone || null }),
    ),
  );

// ***** Helper functions for both ***** //

const sanitizeForMarkdown = (value: string) => value.replace(/\|/g, '\\|');

// Given columns and row data, return an ASCII-style table.
const toTextTable = (
  columns: ColumnType[],
  values: PQDataValue[][],
  options: FormatterOptions,
  formatForMarkdown = false,
): string => {
  const columnWidthMap: number[] = [];
  const headersRow = columns.map(({ name }, index) => {
    const sanitizedName = formatForMarkdown ? sanitizeForMarkdown(name) : name;
    columnWidthMap[index] = sanitizedName.length;
    return sanitizedName;
  });

  const dataRows = values.map((row) =>
    row.map((cell, index) => {
      const displayValue = getPQDisplayText(cell, columns[index], { timezone: options.timezone || null });
      const value = displayValue === '' ? '-' : displayValue;
      const sanitizedValue = formatForMarkdown ? sanitizeForMarkdown(value) : value;

      if (sanitizedValue.length > columnWidthMap[index]) {
        columnWidthMap[index] = sanitizedValue.length;
      }
      return sanitizedValue;
    }),
  );

  const headerAndBodyFormatter = (value: string, index: number) => {
    const isNumericColumn = columns[index].type === CellType.NUMBER;

    return isNumericColumn
      ? `${value.padStart(columnWidthMap[index] + 1, ' ')} `
      : ` ${value.padEnd(columnWidthMap[index] + 1, ' ')}`;
  };

  const separatorFormatter = (length: number, index: number) => {
    const isNumericColumn = columns[index].type === CellType.NUMBER;

    return isNumericColumn && formatForMarkdown ? ` ${''.padEnd(length - 1, '-')}: ` : ` ${''.padEnd(length, '-')} `;
  };

  const headers = `|${headersRow.map(headerAndBodyFormatter).join('|')}|`;
  const separator = `|${columnWidthMap.map(separatorFormatter).join('|')}|`;
  const body = dataRows.map((row) => `|${row.map(headerAndBodyFormatter).join('|')}|`).join('\n');

  return `${headers}\n${separator}\n${body}`;
};

const toJson = (list: any[]) => JSON.stringify(list.filter(Boolean), null, 2);

const toCsv = (columns: ColumnType[], values: string[][]) =>
  formatAsCsvContent(
    columns.map((column) => column.name),
    values,
  );

const toMarkdown = (columns: ColumnType[], values: PQDataValue[][], options: FormatterOptions) => {
  const content = toTextTable(columns, values, options, true);
  const filterText = options.filter ? options.filter + '\n' : '';
  return compact(['```', filterText, content, '```', options.shortUrl]).join('\n') + '\n';
};

/**
 * Format search results as CSV, JSON, markdown, or text.
 *
 * @param logEntries    list of log events
 * @param format        format to use for the output
 * @param settings      user settings
 * @param options       formatting options
 *
 * @returns formatted text
 */
export const formatSearchResults = (
  logEntries: LogEntry[],
  format: K.DATA_FORMAT,
  settings: Settings,
  options = {} as FormatterOptions,
): string => {
  if (logEntries.length === 0) {
    return '';
  }

  switch (format) {
    case K.DATA_FORMAT.CSV: {
      const logObjects = logEntries.map((logEntry) => formatLogEntryAsJson(logEntry, settings, options));
      const columns = getSearchResultColumns(logObjects, settings, options);
      const rows = logObjects.map((obj) => columns.map((column) => obj[column.name as string] ?? ''));
      columns.forEach((col: ColumnType, idx: number) => {
        const sysCol = col.name as SystemField;
        if (K.SystemFieldText[sysCol]) {
          columns[idx].name = K.SystemFieldText[sysCol];
        }
      });

      return toCsv(columns, rows);
    }
    case K.DATA_FORMAT.JSON: {
      const list = logEntries
        .filter(Boolean)
        .map((logEntry) => formatLogEntryAsJson(logEntry, settings, options))
        .filter(Boolean);

      return toJson(list);
    }
    case K.DATA_FORMAT.MARKDOWN: {
      const logObjects = logEntries.map((logEntry) => formatLogEntryAsJson(logEntry, settings, options));
      const columns = getSearchResultColumns(logObjects, settings, options);
      const rows = logObjects.map((obj) => columns.map((column) => obj[column.name as string] ?? ''));

      return toMarkdown(columns, rows, options);
    }
    case K.DATA_FORMAT.TEXT: {
      const content = logEntries
        .filter(Boolean)
        .map((logEntry) => formatLogEntryAsText(logEntry, settings, options))
        .filter(Boolean)
        .join('');

      const filterText = options.filter ? options.filter + '\n' : '';
      return compact([filterText, content, options.shortUrl]).join('\n') + '\n';
    }
  }
};

/**
 * Format Power Query results as CSV, JSON, markdown, or text.
 *
 * @param columns     list of PQ columns
 * @param rows        PQ results data
 * @param format      format to use for the output
 * @param options     formatting options
 *
 * @returns formatted text
 */
export const formatPowerQueryResults = (
  columns: ColumnType[],
  rows: PQDataValue[][],
  format: K.DATA_FORMAT,
  options: FormatterOptions,
): string => {
  if (columns.length === 0 || rows.length === 0) {
    return '';
  }

  switch (format) {
    case K.DATA_FORMAT.CSV: {
      return toCsv(columns, getPowerQueryValues(rows, columns, options));
    }
    case K.DATA_FORMAT.JSON: {
      const list = rows.map((row) =>
        columns.reduce(
          (acc, column, index) => ({
            ...acc,
            [column.name]: getPQValue(row[index]),
          }),
          {},
        ),
      );
      return toJson(list);
    }
    case K.DATA_FORMAT.MARKDOWN: {
      return toMarkdown(columns, rows, options);
    }
    case K.DATA_FORMAT.TEXT: {
      const content = toTextTable(columns, rows, options, false);
      return compact([options.filter, content, options.shortUrl]).join('\n') + '\n';
    }
  }
};
